/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { Headline } from '@/components/Headline/Headline';
import { useLabels } from '@/hooks/useLabels';
import { Text, View } from '@az/starc-ui';
import { useStoreDetails } from '../../api/getStoreDetails';
import styles from './StoreDescription.module.scss';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { useLocale } from '@/hooks/useLocale';
import { interpolateLabel } from '@/features/i18n/utils/interpolateLabel';
import { SmartLink as Link } from '@/utils/smartLink';
import { type ContentStackStorePageBody } from '@/features/contentstack/interface';
import { Fragment } from 'react';
import Head from 'next/head';
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl, isYouTubeUrl } from '@/utils/youtubeUrl';
import { YouTubeFacade } from '@/components/YouTubeFacade/YouTubeFacade';
import { getVideoSchema } from '@/features/common/utils/getVideoSchema';
import { getBaseSchemaUrl } from '@/utils/common';
import { metaTagKey } from '@/constants/seoMetaTags';
// eslint-disable-next-line no-restricted-imports
import parse from 'html-react-parser';

const labelMap = {
  customerService: 'label_locations_customerServicePhoneLabel',
  moreInfo: 'label_locations_storeDescription_moreInfo',
  about: 'label_locations_storeDescription_about',
};

export type StoreDescriptionProps = {
  content: ContentStackStorePageBody['store_description'] | undefined;
  seoCopyBlock: string | undefined;
};

export function StoreDescription({ content, seoCopyBlock }: StoreDescriptionProps) {
  const { video_url, poster_image, upload_date } = content ?? {};
  const labels = useLabels(labelMap);

  const { data: storeDetails } = useStoreDetails();
  const locale = useLocale();

  if (!storeDetails?.address) {
    return null;
  }
  const locationCustomContent = storeDetails.c_autoPartsBody2?.trim();
  const storeDescriptionHeading = `${labels.about} ${storeDetails.name} ${storeDetails.address.city} #${storeDetails.meta.id}`;
  const storeCopyBlock = seoCopyBlock
    ? interpolateLabel(
        seoCopyBlock,
        {
          address: storeDetails.address.line1,
          city: storeDetails.address.city,
          state: storeDetails.address.region,
        },
        true,
      )
    : '';

  const posterImg = poster_image ? poster_image : '/images/video-default-poster-gray.svg';

  const baseUrl = getBaseSchemaUrl(locale);
  const toAbsoluteUrl = (url: string) => (/^https?:\/\//i.test(url) ? url : `${baseUrl}${url}`);
  const isYouTubeVideo = video_url ? isYouTubeUrl(video_url) : false;

  const videoSchema = video_url
    ? getVideoSchema({
        name: storeDescriptionHeading,
        uploadDate: upload_date,
        thumbnailUrl: isYouTubeVideo
          ? getYouTubeThumbnailUrl(video_url)
          : poster_image
            ? toAbsoluteUrl(poster_image)
            : undefined,
        ...(isYouTubeVideo
          ? { embedUrl: getYouTubeEmbedUrl(video_url) }
          : { contentUrl: toAbsoluteUrl(video_url) }),
      })
    : null;

  const videoSchemaHtml = videoSchema ? JSON.stringify(videoSchema).replace(/</g, '\\u003c') : null;

  return (
    <FlexGrid gap={6} flexDirection="column">
      {videoSchemaHtml ? (
        <Head>
          <script
            type="application/ld+json"
            key={metaTagKey('videoSchema')}
            dangerouslySetInnerHTML={{ __html: videoSchemaHtml }}
          />
        </Head>
      ) : null}
      <Headline className={styles.storeDescriptionHeadline}>{storeDescriptionHeading}</Headline>
      <FlexGrid className={styles.storeDescriptionRow} flexWrap="wrap">
        <View className={styles.storeCopyBlockSection}>
          <FlexGrid gap={6} flexDirection="column">
            {storeCopyBlock ? (
              <Text>
                <div
                  className={styles.storeCopyBlock}
                  dangerouslySetInnerHTML={{ __html: storeCopyBlock }}
                />
              </Text>
            ) : null}
            {locationCustomContent ? (
              <Text>
                <div className={styles.locationCustomContent} data-testid="location-custom-content">
                  {parse(locationCustomContent)}
                </div>
              </Text>
            ) : null}
          </FlexGrid>
        </View>
        <View className={styles.storeVideoSection}>
          <View>
            <FlexGrid gap={5} flexDirection="column">
              <View>
                {video_url && isYouTubeUrl(video_url) ? (
                  <YouTubeFacade
                    videoUrl={video_url}
                    title={storeDescriptionHeading}
                    posterImage={poster_image}
                  />
                ) : (
                  <video
                    src={video_url}
                    poster={posterImg}
                    preload="none"
                    controlsList="nodownload"
                    controls
                    width="100%"
                  />
                )}
              </View>
              {storeDetails.tollFreePhone ? (
                <Fragment>
                  <Text>
                    {labels.moreInfo} {labels.customerService}{' '}
                    <span className={styles.storeCopyBlock}>
                      <Link
                        to={storeDetails.tollFreePhone}
                        isPhoneNumber
                        className={styles.storeCopyBlockPhone}
                      >
                        {formatPhoneNumber(storeDetails.tollFreePhone, locale, {
                          stripCountryCode: true,
                        })}
                      </Link>
                    </span>
                  </Text>
                </Fragment>
              ) : null}
            </FlexGrid>
          </View>
        </View>
      </FlexGrid>
    </FlexGrid>
  );
}
